import './PlatformModules.css'

// Modules in priority order: Studio → Chekkouts → Keeply → Relay → People → Namelyft
const modules = [
  {
    id: 'studio',
    label: 'AI App Builder',
    name: 'Studio',
    tagline: 'Describe it. Watch it build. Ship it live.',
    description: 'Studio is your AI-powered app builder. Describe what you want in plain English and watch your site come to life in real-time — complete with real code you own, ready to deploy.',
    features: [
      { text: 'AI-powered building', detail: 'Describe it, watch it build' },
      { text: 'Real code output', detail: 'React, full ownership' },
      { text: 'Live preview', detail: 'See changes in real-time' },
      { text: 'One-click deploy', detail: 'Custom domains, SSL included' },
    ],
    color: 'studio',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
      </svg>
    ),
  },
  {
    id: 'chekkouts',
    label: 'Payments',
    name: 'Chekkouts',
    tagline: 'Accept payments. Manage checkouts. Get paid.',
    description: 'Chekkouts handles payment processing, checkout pages, invoicing, and payouts. Connect your bank, configure your checkout, and start collecting payments — all from your Hubthrill account.',
    features: [
      { text: 'Payment processing', detail: 'Cards, bank transfers, wallets' },
      { text: 'Hosted checkout', detail: 'Customizable, conversion-optimized' },
      { text: 'Recurring billing', detail: 'Subscriptions & invoicing' },
      { text: 'Instant payouts', detail: 'Same-day settlements' },
    ],
    color: 'payments',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  {
    id: 'keeply',
    label: 'Accounting',
    name: 'Keeply',
    tagline: 'Track finances. Stay compliant. See clearly.',
    description: 'Keeply gives you bookkeeping, expense tracking, financial reporting, and tax-ready records. When connected with Chekkouts, your revenue flows in automatically — no manual entry.',
    features: [
      { text: 'Double-entry bookkeeping', detail: 'Automated reconciliation' },
      { text: 'Expense management', detail: 'Receipt scanning, categories' },
      { text: 'Financial statements', detail: 'P&L, balance sheet, cash flow' },
      { text: 'Tax preparation', detail: 'Export-ready reports' },
    ],
    color: 'accounting',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
      </svg>
    ),
  },
  {
    id: 'relay',
    label: 'Chat & Support',
    name: 'Relay',
    tagline: 'Talk to customers. Solve problems. Build loyalty.',
    description: 'Relay provides live chat, help desk, and customer support tools. Embed chat widgets on your Studio sites, manage conversations from a unified inbox, and automate responses.',
    features: [
      { text: 'Live chat widget', detail: 'Embed on any site instantly' },
      { text: 'Unified inbox', detail: 'All conversations in one place' },
      { text: 'Automation', detail: 'Auto-replies and chatbots' },
      { text: 'Customer context', detail: 'Payment history in every chat' },
    ],
    color: 'relay',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  {
    id: 'people',
    label: 'HR & Payroll',
    name: 'People',
    tagline: 'Manage your team. Run payroll. Stay compliant.',
    description: 'People handles employee management, payroll processing, time-off tracking, and HR documents. Payroll costs flow directly into Keeply for seamless financial management.',
    features: [
      { text: 'Employee directory', detail: 'Centralized records' },
      { text: 'Payroll processing', detail: 'Run payroll in minutes' },
      { text: 'Time-off management', detail: 'PTO tracking, approvals' },
      { text: 'Keeply integration', detail: 'Costs sync automatically' },
    ],
    color: 'people',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: 'namelyft',
    label: 'Infrastructure',
    name: 'Namelyft',
    tagline: 'Host, deploy, and scale without the complexity.',
    description: 'Namelyft provides cloud hosting, domain management, and deployment pipelines for your applications. Designed for teams who want infrastructure that works — without hiring a DevOps team.',
    features: [
      { text: 'Managed hosting', detail: 'Auto-scaling, load balancing' },
      { text: 'Domain & DNS', detail: 'Registration, SSL included' },
      { text: 'CI/CD pipelines', detail: 'Git-based deployments' },
      { text: 'Edge network', detail: 'Global CDN, low latency' },
    ],
    color: 'infrastructure',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="2" width="20" height="8" rx="2"/>
        <rect x="2" y="14" width="20" height="8" rx="2"/>
        <circle cx="6" cy="6" r="1" fill="currentColor"/>
        <circle cx="6" cy="18" r="1" fill="currentColor"/>
      </svg>
    ),
  },
]

function PlatformModules() {
  return (
    <section className="platform-modules section" id="modules">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Platform modules</span>
          <h2 className="section-title">
            Everything your business needs.
            <br />
            <span className="gradient-text">Nothing it doesn't.</span>
          </h2>
          <p className="section-subtitle">
            Each module is production-ready and used by real businesses today. 
            Activate what you need from your Hubthrill dashboard — no separate 
            signups, no duplicate data entry.
          </p>
        </div>

        <div className="modules-grid">
          {modules.map((module) => (
            <article key={module.id} className={`module-card module-card-${module.color}`}>
              <div className="module-glow"></div>
              
              <div className="module-header">
                <div className={`module-icon module-icon-${module.color}`}>
                  {module.icon}
                </div>
                <span className="module-label">{module.label}</span>
              </div>

              <h3 className="module-name">{module.name}</h3>
              <p className="module-tagline">{module.tagline}</p>
              
              <p className="module-description">{module.description}</p>
              
              <ul className="module-features">
                {module.features.map((feature, index) => (
                  <li key={index} className="module-feature">
                    <div className="feature-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <div className="feature-text">
                      <span className="feature-main">{feature.text}</span>
                      <span className="feature-detail">{feature.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <a href={`/modules/${module.id}`} className="module-link link-arrow">
                Explore {module.name}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default PlatformModules
